import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MoreHorizontal } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LocationSelect } from "@/components/LocationSelect";

const DONE = { move: "Location updated", sold: "Marked as sold", consumed: "Marked as consumed", pending: "Marked as pending", lost: "Marked as lost", in_cellar: "Back in cellar", restore: "Bottle restored" };

const menuFor = (kind, status) => {
  if (kind === "box") return ["move", "sold", "consumed", "pending", "lost"];
  if (status === "in_cellar") return ["move", "sold", "consumed", "pending", "lost"];
  if (status === "pending") return ["move", "sold", "consumed", "lost"];
  if (status === "deleted") return ["restore"];
  return ["in_cellar", "move"];
};
const LABEL = { move: "Location…", sold: "Sale…", consumed: "Consumed", pending: "Pending", lost: "Lost", in_cellar: "Restore to cellar", restore: "Undo delete" };

export const ItemActions = ({ item, kind, bottles, onDone }) => {
  const [dialog, setDialog] = useState("");
  const targets = kind === "box" ? bottles.filter((b) => b.box_id === item.id && b.status === "in_cellar") : [item];

  const run = async (action, extra = {}) => {
    try {
      if (action === "move") await api.post("/moves", { barcode: item.barcode, location_id: extra.location_id });
      else if (action === "in_cellar") await api.patch(`/bottles/${item.id}/status`, { status: "in_cellar" });
      else if (action === "restore") await api.post("/bottles/restore", { ids: [item.id] });
      else {
        if (!targets.length) return toast.error("No bottles in the cellar in this box");
        for (const b of targets) await api.post("/bottles/action", { barcode: b.barcode, action, ...extra });
      }
      toast.success(DONE[action]);
      setDialog("");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pick = (a) => (a === "move" || a === "sold" ? setDialog(a) : run(a));

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8" data-testid={`actions-${item.barcode}`}><MoreHorizontal className="w-4 h-4 mr-1" />Actions</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {menuFor(kind, item.status).map((a, i) => (
            <div key={a}>
              {i === 2 && <DropdownMenuSeparator />}
              <DropdownMenuItem onSelect={() => pick(a)} data-testid={`action-${a}-${item.barcode}`}>{LABEL[a]}</DropdownMenuItem>
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog("")}>
        <DialogContent className="max-w-md bg-[#161214] border-[#2E252A]" data-testid="item-action-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{dialog === "move" ? "New location" : "Sale"} · <span className="font-mono text-base">{item.barcode}</span></DialogTitle></DialogHeader>
          {dialog === "move" ? <MoveForm onSave={(location_id) => run("move", { location_id })} /> : <SaleForm count={targets.length} onSave={(extra) => run("sold", extra)} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const MoveForm = ({ onSave }) => {
  const [loc, setLoc] = useState("");
  return (
    <div className="space-y-4">
      <LocationSelect value={loc} onChange={setLoc} testId="item-move-location" allowCreate />
      <Button disabled={!loc} onClick={() => onSave(loc)} className="w-full bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="item-move-save">Confirm new location</Button>
    </div>
  );
};

const SaleForm = ({ count, onSave }) => {
  const [sales, setSales] = useState([]);
  const [saleId, setSaleId] = useState("");
  const [box, setBox] = useState("");
  const [price, setPrice] = useState("");
  useEffect(() => {
    api.get("/sales").then((r) => setSales(r.data.filter((s) => !s.closed))).catch(() => setSales([]));
  }, []);
  const save = () => onSave({ sale_id: saleId || null, sale_box_number: box, sale_price: price === "" ? null : Number(price) });
  return (
    <div className="space-y-4">
      <Select value={saleId || "none"} onValueChange={(v) => setSaleId(v === "none" ? "" : v)}>
        <SelectTrigger data-testid="item-sale-select"><SelectValue placeholder="Choose a sale" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No sale selected</SelectItem>
          {sales.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`item-sale-option-${s.id}`}>{s.name}{s.buyer ? ` · ${s.buyer}` : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input value={box} onChange={(e) => setBox(e.target.value)} maxLength={40} placeholder="Sale box number (optional)" data-testid="item-sale-box-input" />
      <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Sale price per bottle (optional)" data-testid="item-sale-price-input" />
      <Button onClick={save} className="w-full bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="item-sale-save">Confirm sale{count > 1 ? ` · ${count} bottles` : ""}</Button>
    </div>
  );
};
